import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API.
 */
class SectionRenderer {
  /** @type {Map<string, string>} */
  #cache = new Map();
  /** @type {Map<string, AbortController>} */
  #abortControllersBySectionId = new Map();
  /** @type {Map<string, Promise<string>>} */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * @param {string} sectionId
   * @param {Object} [options]
   * @param {boolean} [options.cache]
   * @param {'hydration'|'full'} [options.mode]
   * @param {URL} [options.url]
   * @param {boolean} [options.injectStylesheet]
   * @returns {Promise<string>}
   */
  async renderSection(sectionId, options) {
    const {
      cache = !Shopify.designMode,
      mode = 'full',
      url,
      injectStylesheet = false,
    } = options ?? {};

    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);
      await morphSection(sectionId, sectionHTML, { mode, injectStylesheet });
    }

    return sectionHTML;
  }

  /** @param {string} sectionId */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) existingAbortController.abort();
  }

  /**
   * @param {string} sectionId
   * @param {boolean} useCache
   * @param {URL} url
   * @returns {Promise<string>}
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);
      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl).then((response) => response.text());
    this.#pendingPromises.set(sectionUrl, pendingPromise);

    const sectionHTML = await pendingPromise;
    this.#pendingPromises.delete(sectionUrl);
    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  /** Aborts an existing morph for a section. */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;
      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * @param {string} sectionId
 * @param {URL} url
 * @returns {string}
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();
  return url.toString();
}

/** @param {string} sectionId */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/** @param {string} sectionId */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/** @param {Element} element */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @param {string} sectionId
 * @param {string} html
 * @param {Object} [options]
 * @param {'hydration'|'full'} [options.mode]
 * @param {boolean} [options.injectStylesheet]
 */
export async function morphSection(sectionId, html, options = {}) {
  const { mode = 'full', injectStylesheet = false } = options;
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) throw new Error(`Section ${sectionId} not found`);
  if (!newElement) throw new Error(`Section ${sectionId} not found in the section rendering response`);

  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    hydrationMode: mode === 'hydration',
  });

  if (injectStylesheet) injectSectionStylesheet(fragment, existingElement);
}

/**
 * @param {Document} fragment
 * @param {HTMLElement} sectionElement
 */
function injectSectionStylesheet(fragment, sectionElement) {
  const newStylesheet = fragment.querySelector('style[data-section-stylesheet]');
  if (!newStylesheet) return;

  const existingStylesheet = sectionElement.querySelector('style[data-section-stylesheet]');

  if (existingStylesheet) {
    existingStylesheet.textContent = newStylesheet.textContent;
  } else {
    sectionElement.prepend(newStylesheet);
  }
}

export const sectionRenderer = new SectionRenderer();
